// services/pricingWithLeadService.ts
// Serwis do obsługi wycen z wymogiem podania danych kontaktowych

import { supabase } from '@/lib/supabaseClient';
import { chatbotCalculator } from '@/services/chatbotCalculatorService';

interface PricingRequest {
  material: string;
  thickness: number;
  width: number;
  height: number;
  quantity: number;
  processing?: {
    cutting?: boolean;
    bending?: boolean; 
    printing?: boolean;
  };
}

interface LeadData {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
}

interface PricingSession {
  id: string;
  pricingRequest: PricingRequest | null;
  leadData: LeadData;
  status: 'collecting_specs' | 'specs_ready' | 'collecting_lead' | 'complete';
  calculatedPrice?: number;
  timestamp: Date;
  conversationId?: string;
}

class PricingWithLeadService {
  private sessions: Map<string, PricingSession> = new Map();

  // Utwórz nową sesję wyceny
  createSession(conversationId: string): PricingSession {
    const session: PricingSession = {
      id: crypto.randomUUID(),
      pricingRequest: null,
      leadData: {},
      status: 'collecting_specs',
      timestamp: new Date(),
      conversationId
    };
    
    this.sessions.set(conversationId, session);
    return session;
  }

  // Pobierz lub utwórz sesję
  getOrCreateSession(conversationId: string): PricingSession {
    if (this.sessions.has(conversationId)) {
      return this.sessions.get(conversationId)!;
    }
    return this.createSession(conversationId);
  }

  // Sprawdź czy wiadomość zawiera specyfikację produktu
  extractPricingRequest(message: string): PricingRequest | null {
    const lowerMessage = message.toLowerCase();
    
    // Wyrażenia regularne do wyciągania danych
    const dimensionRegex = /(\d+)\s*[xX×]\s*(\d+)\s*(?:mm)?/;
    const quantityRegex = /(\d+)\s*(?:szt|sztuk)/;
    const thicknessRegex = /(\d+)\s*mm/;
    
    const dimensions = lowerMessage.match(dimensionRegex);
    const quantity = lowerMessage.match(quantityRegex);
    const thickness = lowerMessage.match(thicknessRegex);
    
    // Materiały
    const materials = [
      { name: 'Plexi bezbarwna', keywords: ['transparentn', 'przezroczyst', 'bezbarwn', 'clear'] },
      { name: 'Plexi mleczna', keywords: ['mleczn', 'opalow', 'matow', 'opal'] },
      { name: 'Plexi czarna', keywords: ['czarn', 'black'] },
      { name: 'Plexi kolorowa', keywords: ['kolorow', 'barwion', 'czerwon', 'niebiesk', 'zielon'] },
      { name: 'PCV', keywords: ['pcv', 'pvc', 'forex'] },
      { name: 'Dibond', keywords: ['dibond', 'kompozyt', 'alu'] }
    ];
    
    let material = null;
    for (const mat of materials) {
      if (mat.keywords.some(keyword => lowerMessage.includes(keyword))) {
        material = mat.name;
        break;
      }
    }
    
    // Jeśli nie ma materiału ale wspomina plexi, domyślnie transparentna
    if (!material && (lowerMessage.includes('plexi') || lowerMessage.includes('pleksi'))) {
      material = 'Plexi bezbarwna';
    }
    
    // Jeśli mamy wszystkie dane, zwróć obiekt
    if (dimensions && material && thickness) {
      return {
        material,
        thickness: parseInt(thickness[1]),
        width: parseInt(dimensions[1]),
        height: parseInt(dimensions[2]),
        quantity: quantity ? parseInt(quantity[1]) : 1,
        processing: {
          cutting: true,
          bending: lowerMessage.includes('gię') || lowerMessage.includes('gni'),
          printing: lowerMessage.includes('druk') || lowerMessage.includes('print')
        }
      };
    }
    
    return null;
  }

  // Sprawdź czy wiadomość zawiera dane kontaktowe
  extractLeadData(message: string): Partial<LeadData> {
    const leadData: Partial<LeadData> = {};
    
    // Email
    const emailMatch = message.match(/\b[\w._%+-]+@[\w.-]+\.[A-Z|a-z]{2,}\b/i);
    if (emailMatch) {
      leadData.email = emailMatch[0];
    }
    
    // Telefon (polski format)
    const phoneMatch = message.match(/(?:\+48)?\s*(\d{3}[\s-]?\d{3}[\s-]?\d{3}|\d{2}[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2})/);
    if (phoneMatch) {
      leadData.phone = phoneMatch[0].replace(/[\s-]/g, '');
    }
    
    // Imię i nazwisko
    const namePatterns = [
      /nazywam się\s+([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+(?:\s+[A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)?)/i,
      /jestem\s+([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+(?:\s+[A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)?)/i,
      /moje imię to\s+([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)/i,
      /([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+\s+[A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)(?:\s+(?:[\w._%+-]+@|tel|telefon|\d{3}))/i
    ];
    
    for (const pattern of namePatterns) {
      const match = message.match(pattern);
      if (match) {
        leadData.name = match[1];
        break;
      }
    }
    
    // Firma
    const companyPatterns = [
      /firma\s+([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+(?:\s+[A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)*)/i,
      /z\s+firmy\s+([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+(?:\s+[A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)*)/i,
      /reprezentuję\s+([A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+(?:\s+[A-ZŁŚŻŹĆŃĄĘÓ][a-złśżźćńąęó]+)*)/i
    ];
    
    for (const pattern of companyPatterns) {
      const match = message.match(pattern);
      if (match) {
        leadData.company = match[1];
        break;
      }
    }
    
    return leadData;
  }

  // Procesuj wiadomość i zaktualizuj sesję
  async processMessage(conversationId: string, message: string): Promise<{
    requiresLeadData: boolean;
    missingFields?: string[];
    pricingReady: boolean;
    response?: string;
  }> {
    const session = this.getOrCreateSession(conversationId);
    
    // Sprawdź czy wiadomość zawiera specyfikację produktu
    const pricingRequest = this.extractPricingRequest(message);
    if (pricingRequest && !session.pricingRequest) {
      session.pricingRequest = pricingRequest;
      session.status = 'specs_ready';
      
      // Oblicz cenę ale nie pokazuj jej jeszcze
      const result = await chatbotCalculator.calculate(pricingRequest);
      session.calculatedPrice = result.totalPrice;
    }
    
    // Sprawdź czy wiadomość zawiera dane kontaktowe
    const leadData = this.extractLeadData(message);
    if (leadData.name) session.leadData.name = leadData.name;
    if (leadData.email) session.leadData.email = leadData.email;
    if (leadData.phone) session.leadData.phone = leadData.phone;
    if (leadData.company) session.leadData.company = leadData.company;
    
    // Jeśli mamy specyfikację ale brakuje danych kontaktowych
    if (session.status === 'specs_ready' || session.status === 'collecting_lead') {
      const missingFields = [];
      
      if (!session.leadData.name) missingFields.push('imię i nazwisko');
      if (!session.leadData.email && !session.leadData.phone) {
        missingFields.push('email lub telefon');
      }
      
      if (missingFields.length > 0) {
        session.status = 'collecting_lead';
        return {
          requiresLeadData: true,
          missingFields,
          pricingReady: false,
          response: this.getLeadRequestMessage(missingFields, session)
        };
      } else {
        // Mamy wszystkie dane - zapisz lead i pokaż cenę
        await this.saveLead(session);
        session.status = 'complete';
        
        return {
          requiresLeadData: false,
          pricingReady: true,
          response: await this.getPricingWithThankYou(session)
        };
      }
    }
    
    return {
      requiresLeadData: false,
      pricingReady: false
    };
  }

  // Wiadomość z prośbą o dane
  private getLeadRequestMessage(missingFields: string[], session: PricingSession): string {
    let message = `📊 **Świetnie! Mam wycenę dla Ciebie!**\n\n`;
    
    if (session.pricingRequest) {
      message += `✅ **Parametry:**\n`;
      message += `• Materiał: ${session.pricingRequest.material} ${session.pricingRequest.thickness}mm\n`;
      message += `• Wymiary: ${session.pricingRequest.width} × ${session.pricingRequest.height} mm\n`;
      message += `• Ilość: ${session.pricingRequest.quantity} szt.\n\n`;
    }
    
    message += `🎯 **Zanim podam cenę, potrzebuję Twoich danych kontaktowych:**\n\n`;
    
    if (missingFields.includes('imię i nazwisko')) {
      message += `👤 **Imię i nazwisko**\n`;
    }
    
    if (missingFields.includes('email lub telefon')) {
      message += `📧 **Email** lub 📱 **Telefon**\n`;
    }
    
    message += `\n💡 *Dlaczego prosimy o dane?*\n`;
    message += `• Wyślemy pełną ofertę na maila\n`;
    message += `• Nasz handlowiec może doprecyzować szczegóły\n`;
    message += `• Otrzymasz rabat dla stałych klientów\n\n`;
    
    message += `**Przykład:** "Jan Kowalski, jan@firma.pl, 600 123 456"`;
    
    return message;
  }

  // Zapisz lead w bazie
  private async saveLead(session: PricingSession): Promise<void> {
    try {
      // Przygotuj notatkę z zapytaniem
      const notes = `Zapytanie z chatbota:
- Produkt: ${session.pricingRequest?.material} ${session.pricingRequest?.thickness}mm
- Wymiary: ${session.pricingRequest?.width} × ${session.pricingRequest?.height} mm  
- Ilość: ${session.pricingRequest?.quantity} szt.
- Wycena: ${session.calculatedPrice?.toFixed(2)} zł netto
- Źródło: Chatbot AI`;

      // Zapisz w bazie Supabase
      const { data: lead, error } = await supabase
        .from('leads')
        .insert({
          contact_name: session.leadData.name || 'Klient chatbota',
          email: session.leadData.email || null,
          phone: session.leadData.phone || null,
          company_name: session.leadData.company || 'Nieznana',
          source: 'chatbot_pricing',
          notes: notes,
          status: 'new',
          value: session.calculatedPrice
        })
        .select()
        .single();
      
      if (lead) {
        console.log('✅ Lead zapisany:', lead);
        
        // Wyślij powiadomienie email do handlowców
        try {
          await fetch('https://api.plexisystem.pl/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: 'lukasz@plexisystem.pl',
              subject: `🔥 GORĄCY LEAD - ${session.leadData.name} - ${session.calculatedPrice?.toFixed(2)} zł`,
              html: `
                <h2>🎯 Nowy lead z wyceny chatbota!</h2>
                <p><strong>Klient zainteresowany zakupem!</strong></p>
                
                <h3>Dane kontaktowe:</h3>
                <ul>
                  <li><strong>Imię:</strong> ${session.leadData.name}</li>
                  <li><strong>Email:</strong> ${session.leadData.email || 'Brak'}</li>
                  <li><strong>Telefon:</strong> ${session.leadData.phone || 'Brak'}</li>
                  <li><strong>Firma:</strong> ${session.leadData.company || 'Nieznana'}</li>
                </ul>
                
                <h3>Zapytanie:</h3>
                <ul>
                  <li><strong>Materiał:</strong> ${session.pricingRequest?.material} ${session.pricingRequest?.thickness}mm</li>
                  <li><strong>Wymiary:</strong> ${session.pricingRequest?.width} × ${session.pricingRequest?.height} mm</li>
                  <li><strong>Ilość:</strong> ${session.pricingRequest?.quantity} szt.</li>
                  <li><strong>Wycena:</strong> ${session.calculatedPrice?.toFixed(2)} zł netto</li>
                </ul>
                
                <hr>
                <p><strong>⚡ DZIAŁAJ SZYBKO!</strong> Klient otrzymał wycenę i czeka na kontakt!</p>
                <p><a href="https://app.plexisystem.pl/crm/leads/${lead.id}">Zobacz w CRM</a></p>
              `
            })
          });
          console.log('📧 Email wysłany do handlowców!');
        } catch (emailError) {
          console.error('Błąd wysyłania emaila:', emailError);
        }
      }
    } catch (error) {
      console.error('Błąd zapisu leada:', error);
    }
  }

  // Wycena z podziękowaniem - BEZ POKAZYWANIA LOGIKI KALKULACJI
  private async getPricingWithThankYou(session: PricingSession): Promise<string> {
    if (!session.pricingRequest || !session.calculatedPrice) {
      return 'Wystąpił błąd podczas kalkulacji.';
    }
    
    const result = await chatbotCalculator.calculate(session.pricingRequest);
    let message = `✅ **Dziękuję ${session.leadData.name}!**\n\n`;
    message += `Twoje dane zostały zapisane. Nasz handlowiec skontaktuje się z Tobą wkrótce.\n\n`;
    
    // Użyj KRÓTKIEJ wersji wyceny - bez logiki kalkulacji!
    message += chatbotCalculator.formatResult(result, session.pricingRequest);
    
    message += `\n\n📞 **Potrzebujesz szybszej odpowiedzi?**\n`;
    message += `Zadzwoń bezpośrednio: **884 042 107**\n\n`;
    
    if (session.leadData.email) {
      message += `📧 Pełna oferta została wysłana na: **${session.leadData.email}**`;
    }
    
    return message;
  }

  // Wyczyść sesję
  clearSession(conversationId: string): void {
    this.sessions.delete(conversationId);
  }
}

export const pricingWithLeadService = new PricingWithLeadService();
